import { useState, useEffect } from 'react';
import { isAuthenticated, logout } from './lib/auth';
import LoginScreen from './components/LoginScreen';
import OfflineBar from './components/OfflineBar';
import Dashboard from './components/Dashboard';
import TypingModule from './modules/typing/TypingModule';
import ComputerModule from './modules/computer/ComputerModule';
import EnglishModule from './modules/english/EnglishModule';

type TabId = 'home' | 'typing' | 'computer' | 'english';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  {
    id: 'home',
    label: 'خانه',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: 'typing',
    label: 'تایپ',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <line x1="6" y1="8" x2="6" y2="8" />
        <line x1="10" y1="8" x2="10" y2="8" />
        <line x1="14" y1="8" x2="14" y2="8" />
        <line x1="18" y1="8" x2="18" y2="8" />
        <line x1="6" y1="12" x2="6" y2="12" />
        <line x1="10" y1="12" x2="10" y2="12" />
        <line x1="14" y1="12" x2="14" y2="12" />
        <line x1="18" y1="12" x2="18" y2="12" />
        <line x1="8" y1="16" x2="16" y2="16" />
      </svg>
    ),
  },
  {
    id: 'computer',
    label: 'کامپیوتر',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    id: 'english',
    label: 'انگلیسی',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        <line x1="8" y1="7" x2="16" y2="7" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </svg>
    ),
  },
];

function TabContent({ tab }: { tab: TabId }) {
  switch (tab) {
    case 'home':
      return <Dashboard />;
    case 'typing':
      return <TypingModule />;
    case 'computer':
      return <ComputerModule />;
    case 'english':
      return <EnglishModule />;
  }
}

export default function App() {
  const [currentTab, setCurrentTab] = useState<TabId>('home');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    // Check auth status on mount
    const resolveAuth = async () => {
      const localAuth = isAuthenticated();
      if (!localAuth) {
        setAuthed(false);
        return;
      }

      try {
        const res = await fetch('/api/auth/verify', {
          method: 'GET',
          credentials: 'include',
        });

        if (!res.ok) {
          logout();
          setAuthed(false);
          return;
        }

        setAuthed(true);
      } catch {
        // If API is temporarily unavailable (or local dev without backend), keep cached auth
        setAuthed(true);
      }
    };

    resolveAuth();
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Still checking auth
  if (authed === null) {
    return null;
  }

  // Not authenticated — show login
  if (!authed) {
    return <LoginScreen onLogin={() => setAuthed(true)} />;
  }

  return (
    <div className="app-shell">
      {!isOnline && <OfflineBar />}

      <main className="app-content">
        <TabContent tab={currentTab} />
      </main>

      <nav className="bottom-nav">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`nav-tab ${currentTab === tab.id ? 'active' : ''}`}
            onClick={() => setCurrentTab(tab.id)}
            aria-label={tab.label}
          >
            <span className="nav-icon">{tab.icon}</span>
            <span className="nav-label">{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
